<template>
<div class="start">

    <section class="band band--auftakt">
        <div class="innen">
            <h1>Funkverkehr üben, bevor das Gerät in der Hand liegt.</h1>
            <p class="lead">
                Funktrainer ist eine Übungsumgebung für den Funkverkehr im Zivilschutz.
                Gerät und Sprechregeln werden im Browser gelernt, damit die Zeit am
                echten Funkgerät für Funkverkehr draufgeht und nicht für die Suche
                nach dem Kanalwahlschalter.
            </p>
            <div class="aktionen">
                <RouterLink to="/training" class="knopf knopf--haupt">Training starten</RouterLink>
                <a href="#grenzen" class="knopf knopf--rand">Was es nicht tut</a>
            </div>
        </div>
    </section>

    <section class="band">
        <div class="innen">
            <h2>Warum es das gibt</h2>
            <p>
                Am Ausbildungstag sind die Geräte knapp und die Zeit kurz. Wer die
                Bedienung erst dort lernt, verbraucht sie fürs Suchen. Funktrainer
                zieht diesen Teil nach vorn: Bedienung und Sprechregeln sitzen,
                bevor der erste echte Funkspruch gesendet wird.
            </p>
        </div>
    </section>

    <section class="band band--geraet">
        <div class="innen modul">
            <div class="modul__text">
                <h2>Das Funkgerät</h2>
                <p>
                    Ein simuliertes Handfunkgerät. Ein- und ausschalten, Lautstärke,
                    Kanal über die Nummer oder über die Liste, Menü, Sprechtaste,
                    Akku.
                </p>
                <p>
                    Falsche Bedienung erzeugt keine Fehlermeldung, sondern das, was
                    wirklich passieren würde: Wer auf einem ausgeschalteten Gerät
                    sendet, erzeugt Stille. Wer den falschen Kanal wählt, bekommt
                    keine Antwort.
                </p>
                <dl class="modi">
                    <dt>Gerät kennenlernen</dt>
                    <dd>19 Themen, 38 Schritte. Das Gerät läuft, jeder Schritt zeigt dafür den passenden Bildschirm.</dd>
                    <dt>Lernmodus</dt>
                    <dd>9 Schritte, die erst weitergehen, wenn du die Handlung wirklich ausgeführt hast.</dd>
                    <dt>Anfänger</dt>
                    <dd>Aufgaben ohne Anleitung.</dd>
                </dl>
                <RouterLink to="/training" class="weiter">Zum Funkgerät</RouterLink>
            </div>

            <figure class="probe">
                <!-- Das echte Display-Bauteil, nicht nachgebaut: so kann die
                     Abbildung nicht vom Geraet abweichen. inert + aria-hidden,
                     weil die Tasten hier nur gezeigt und nicht bedient werden. -->
                <div class="geraet" inert aria-hidden="true">
                    <span class="geraet__antenne"></span>
                    <div class="geraet__chassis">
                        <span class="geraet__power"></span>
                        <span class="geraet__volume"></span>
                        <Display
                            :isOn="true"
                            :channel="kanal"
                            commMode="D"
                            :volume="4"
                            :battery="78"
                            :isTransmitting="false"
                            :currentList="[]"
                            :listIndex="0"
                            screen=""
                            pendingMode="" />
                    </div>
                    <span class="geraet__ptt">PTT</span>
                </div>
                <figcaption>18 Kanäle, Dir und Grp, als Liste oder über die vierstellige Nummer.</figcaption>
            </figure>
        </div>
    </section>

    <section class="band band--sprechregeln">
        <div class="innen modul modul--gedreht">
            <div class="modul__text">
                <h2>Die Sprechregeln</h2>
                <p>
                    Ein Funkspruch besteht aus Bausteinen in fester Reihenfolge:
                    Adressat, Absender, Meldung, Redewendung. Die Reihenfolge ist die
                    Übung. Du legst die Bausteine, prüfst auf Knopfdruck, und die
                    Rückmeldung nennt die Stelle — keine Note, keine Punktzahl.
                </p>
                <p>
                    Fachgrundlage ist der Behelf Sprechregeln des BABS in der Ausgabe
                    2025-04. Die Inhalte stammen daraus und werden nicht erfunden.
                </p>
                <RouterLink to="/sprechregeln" class="weiter">Zu den Sprechregeln</RouterLink>
            </div>

            <figure class="probe" aria-label="Beispiel eines zusammengesetzten Funkspruchs">
                <ol class="spruch">
                    <li><span class="rolle">Adressat</span>An KP Front</li>
                    <li><span class="rolle">Absender</span>von Pionierzug</li>
                    <li><span class="rolle">Inhalt</span>Keller Schulhaus ausgepumpt</li>
                    <li><span class="rolle">Redewendung</span>antworten</li>
                </ol>
                <figcaption>128 Sprüche aus 13 Rufnamen und 34 Meldungen.</figcaption>
            </figure>
        </div>
    </section>

    <section class="band band--lage">
        <div class="innen">
            <h2>Allein oder in der Klasse</h2>
            <div class="spalten">
                <div>
                    <h3>Vor dem Kurs, allein</h3>
                    <p>
                        Eigenes Notebook oder Tablet, eigene Zeit. Es muss niemand
                        danebenstehen und erklären.
                    </p>
                </div>
                <div>
                    <h3>Im Unterricht</h3>
                    <p>
                        Läuft auf den Geräten im Kurslokal, während der Ausbildner
                        spricht oder Aufgaben verteilt. Kein Umschalten zwischen
                        Betriebsarten, keine Anmeldung, keine Vorbereitung.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <section class="band band--grenzen" id="grenzen">
        <div class="innen">
            <h2>Was dieses Werkzeug nicht tut</h2>
            <ul class="grenzen">
                <li>
                    <strong>Keine Noten.</strong> Kein Bestanden, kein Nicht bestanden.
                    Fortschritt wird angezeigt, nicht bewertet.
                </li>
                <li>
                    <strong>Kein Konto, keine Daten.</strong> Keine Anmeldung, kein
                    Server. Was du tust, bleibt im Browser und ist beim Neuladen weg.
                </li>
                <li>
                    <strong>Kein Anschluss an POLYCOM</strong> und an kein anderes
                    Funknetz. Reine Simulation.
                </li>
                <li>
                    <strong>Keine KI.</strong> Was fachlich richtig ist, entscheidet
                    der Behelf, nicht ein Modell.
                </li>
                <li>
                    <strong>Kein Ersatz für die Ausbildung am echten Gerät.</strong>
                    Eine Ergänzung davor.
                </li>
            </ul>
        </div>
    </section>

    <section class="band">
        <div class="innen">
            <h2>Stand</h2>
            <p>
                Das Funkgerät ist benutzbar; die Arbeit daran ruht zurzeit. Die
                Sprechregeln sind in Arbeit: der Grundschema-Funkspruch steht, weitere
                Übungsarten kommen.
            </p>
            <p class="gedaempft">
                Geplant, aber noch nicht gebaut: die Buchstabiertabelle zum Üben; eine
                Gegenstation, deren Antwort mit Lücken ankommt, damit WIEDERHOLEN und
                ICH BUCHSTABIERE überhaupt nötig werden; austauschbare Kanallisten pro
                Kanton.
            </p>
        </div>
    </section>

    <footer class="band band--kolophon">
        <div class="innen">
            <p>
                Keine amtliche Publikation des BABS oder eines Kantons. Fachgrundlage:
                Behelf Sprechregeln, Ausgabe 2025-04. Die Zuordnung der Redewendungen
                ist aus der Vorlage rekonstruiert und noch gegen den Behelf zu prüfen.
            </p>
        </div>
    </footer>

</div>
</template>

<script>
import { ref, onMounted } from 'vue'
import Display from '@/components/Display.vue'

export default {
    components: { Display },
    setup() {
        // Der eine gestaltete Moment auf dieser Seite: das Geraet steht
        // zuerst auf "Nicht in Kom." und tritt dann in einen Kanal ein —
        // dieselbe Anzeige wie beim echten Wechsel. Wer Bewegung abgestellt
        // hat, sieht sofort den Kanal.
        const kanal = ref(null)

        onMounted(() => {
            const sparsam = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            if (sparsam) {
                kanal.value = '9003'
                return
            }
            setTimeout(() => { kanal.value = '9003' }, 900)
        })

        return { kanal }
    }
}
</script>

<style scoped>
/* #app zentriert global allen Text — hier wieder abwaehlen.
   Richtung «Klartext»: ein blauer Auftakt als einzige grosse Farbflaeche,
   darunter weisse, gerundete Tafeln mit weichem Schatten. */
.start {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    max-width: 1080px;
    padding: 2.25rem 2.5rem 3rem;
    text-align: left;
}

.band {
    padding: 2rem 2.25rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
}

.innen {
    max-width: none;
}

/* Leichter Verlauf im Blau, damit die grosse Flaeche nicht stumpf wirkt. */
.band--auftakt {
    padding: 3.25rem 2.75rem 3.5rem;
    background: linear-gradient(135deg, #0A3FB0 0%, var(--zs-blau) 55%, #002A85 100%);
    color: #ffffff;
    background: var(--zs-blau);
    border-color: var(--zs-blau);
}

.band--kolophon {
    padding: 0.5rem 0.25rem 0;
    background: transparent;
    border: none;
    box-shadow: none;
}

/* ─── Typografie ──────────────────────────────────────────── */

h1 {
    max-width: 18ch;
    margin: 0 0 1.25rem;
    font-size: clamp(2rem, 4.5vw, 3.1rem);
    font-weight: 750;
    line-height: 1.08;
    letter-spacing: -0.025em;
    text-wrap: balance;
}

h2 {
    margin: 0 0 0.85rem;
    font-size: 1.35rem;
    font-weight: 750;
    letter-spacing: -0.015em;
}

h3 {
    margin: 0 0 0.4rem;
    font-size: 1.05rem;
    font-weight: 650;
}

p {
    max-width: 68ch;
    margin: 0 0 1rem;
    font-size: 1.02rem;
    line-height: 1.6;
}

p:last-child {
    margin-bottom: 0;
}

/* Auf Blau wird der Nebenton aus dem Blau aufgehellt, nie grau. */
.lead {
    max-width: 60ch;
    font-size: 1.15rem;
    line-height: 1.55;
    color: #D6DFF3;
}

.gedaempft {
    color: var(--zs-grau);
}

/* ─── Knöpfe ──────────────────────────────────────────────── */

.aktionen {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 2rem;
}

.knopf {
    display: inline-flex;
    align-items: center;
    min-height: 2.75rem;
    padding: 0.55rem 1.25rem;
    font-size: 1rem;
    font-weight: 650;
    text-decoration: none;
    border-radius: var(--zs-ecke);
    transition: background-color 0.15s ease-out, border-color 0.15s ease-out;
}

/* Schrift auf diesem Orange ist immer Tinte, nie Weiss — Weiss haelt
   darauf in keiner Groesse den Kontrast. */
.knopf--haupt {
    color: var(--zs-tinte);
    background: var(--zs-orange);
}

.knopf--haupt:hover {
    background: #ffa033;
}

.knopf--rand {
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.5);
}

.knopf--rand:hover {
    border-color: #ffffff;
}

.band--auftakt :focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* ─── Modulabschnitte ─────────────────────────────────────── */

.modul {
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
    gap: 3rem;
    align-items: start;
}

.modul--gedreht .modul__text {
    order: 2;
}

.modi {
    margin: 1.5rem 0;
    border-top: 1px solid var(--zs-rahmen);
}

.modi dt {
    margin-top: 0.75rem;
    font-weight: 650;
}

.modi dd {
    max-width: 60ch;
    margin: 0.15rem 0 0;
    padding-bottom: 0.75rem;
    line-height: 1.5;
    color: var(--zs-grau);
    border-bottom: 1px solid var(--zs-rahmen);
}

.weiter {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 650;
    color: var(--zs-blau);
    text-decoration: none;
}

.weiter::after {
    content: '→';
    transition: transform 0.15s;
}

.weiter:hover::after {
    transform: translateX(3px);
}

.weiter:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* ─── Proben: echte Ausschnitte statt Schmuck ─────────────── */

.probe {
    margin: 0;
}

.probe figcaption {
    margin-top: 0.9rem;
    font-size: 0.9rem;
    line-height: 1.45;
    color: var(--zs-grau);
}

/* Kleines Geraet: Gehaeuse und Bedienteile in denselben Hardwarefarben
   wie im Funkgeraet — das Geraet ist ein abgebildeter Gegenstand und
   benutzt die Projektpalette bewusst nicht. */
/* Verkleinert ueber zoom und nicht ueber transform: zoom rechnet die
   Flaeche im Layout mit, transform wuerde den vollen Platz stehen lassen.
   Die Innenmasse von Display.vue sind in px gesetzt, --w allein
   herunterzusetzen wuerde das Tastenfeld aus dem Gehaeuse schieben. */
.geraet {
    position: relative;
    width: 292px;
    margin: 0 0 0 auto;
    padding-top: 26px;
    zoom: 0.62;
}

.geraet__chassis {
    position: relative;
    padding: 44px 26px 26px;
    background: rgb(93, 94, 98);
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.geraet__antenne {
    position: absolute;
    top: 0;
    right: 44px;
    width: 14px;
    height: 32px;
    background: linear-gradient(180deg, #2a2d33, #4a4d54);
    border-radius: 7px 7px 2px 2px;
}

.geraet__power {
    position: absolute;
    top: 12px;
    right: 24px;
    width: 26px;
    height: 26px;
    background: #000000;
    border-radius: 5px;
    box-shadow: inset 0 0 0 1px #000, 0 2px 4px rgba(0, 0, 0, 0.3);
}

.geraet__volume {
    position: absolute;
    top: 14px;
    left: 24px;
    width: 54px;
    height: 24px;
    background: linear-gradient(180deg, #3a3d44, #22252a);
    border-radius: 12px;
}

.geraet__ptt {
    position: absolute;
    top: 150px;
    right: -14px;
    width: 18px;
    padding: 26px 0;
    background: linear-gradient(180deg, #2a2f37, #1a1d22);
    border-radius: 0 8px 8px 0;
    color: #cccccc;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-align: center;
    writing-mode: vertical-rl;
}

/* Der Beispielspruch ist Funktext: feste Laufweite, Rolle davor. */
.spruch {
    margin: 0;
    padding: 0;
    list-style: none;
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

.spruch li {
    display: flex;
    align-items: baseline;
    gap: 1rem;
    padding: 0.65rem 0.9rem;
    font-family: var(--zs-funkschrift);
    font-size: 1rem;
}

.spruch li + li {
    border-top: 1px solid var(--zs-rahmen);
}

.rolle {
    flex: 0 0 7.5rem;
    font-family: 'Archivo', system-ui, sans-serif;
    font-size: 0.7rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

/* ─── Lage und Grenzen ────────────────────────────────────── */

.spalten {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2.5rem;
    margin-top: 1.25rem;
}

.grenzen {
    max-width: 72ch;
    margin: 1rem 0 0;
    padding: 0;
    list-style: none;
}

.grenzen li {
    padding: 0.75rem 0;
    line-height: 1.55;
    color: var(--zs-grau);
    border-top: 1px solid var(--zs-rahmen);
}

.grenzen strong {
    font-weight: 650;
    color: var(--zs-tinte);
}

.band--kolophon p {
    max-width: 72ch;
    font-size: 0.85rem;
    line-height: 1.55;
    color: var(--zs-grau);
}

@media (max-width: 900px) {
    .start {
        padding: 1.5rem 16px 2.5rem;
    }
}

@media (max-width: 760px) {
    .geraet {
        margin: 0;
        zoom: 0.55;
    }

    .modul,
    .spalten {
        grid-template-columns: minmax(0, 1fr);
        gap: 2rem;
    }

    .modul--gedreht .modul__text {
        order: 0;
    }

    .band {
        padding: 1.5rem 1.25rem;
    }

    .band--auftakt {
        padding: 2rem 1.25rem;
    }
}

@media (prefers-reduced-motion: reduce) {
    .knopf,
    .weiter::after {
        transition: none;
    }
}
</style>
